import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './FamilyMigrationMap.css';
import axios from 'axios';

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const FamilyMigrationMap = ({ documents, conversationId, familyName = "Family" }) => {
  const [locations, setLocations] = useState([]);
  const [migrations, setMigrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGeneration, setSelectedGeneration] = useState('all');
  const [mapType, setMapType] = useState('markers');
  const mapRef = useRef(null);

  useEffect(() => {
    fetchMapData();
  }, [conversationId, documents]);

  const fetchMapData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        '/api/map/analyze',
        { 
          documentIds: documents.map(d => d._id),
          conversationId 
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      if (response.data.locations) {
        setLocations(response.data.locations);
        setMigrations(response.data.migrations || []);
      }
    } catch (error) {
      console.error('Error fetching map data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGenerationColor = (generation) => {
    const colors = {
      '1': '#FF0000',
      '2': '#FF6600',
      '3': '#FFCC00',
      '4': '#33CC33',
      '5': '#3366FF',
      '6': '#9933FF',
      '7': '#FF33CC',
      'unknown': '#666666'
    };
    return colors[generation] || colors['unknown'];
  };

  const createCustomIcon = (location) => {
    const color = getGenerationColor(location.generation);
    return L.divIcon({
      className: 'custom-marker',
      html: `
        <div class="marker-wrapper">
          <div class="marker-icon" style="background-color: ${color};">
            <span class="generation-label">${location.generation || '?'}</span>
          </div>
          <div class="marker-label">${location.shortName || ''}</div>
        </div>
      `,
      iconSize: [30, 40],
      iconAnchor: [15, 40],
      popupAnchor: [0, -40]
    });
  };

  const filteredLocations = selectedGeneration === 'all' 
    ? locations 
    : locations.filter(loc => loc.generation === selectedGeneration);

  const createMigrationPaths = () => {
    const paths = [];
    const sortedLocations = [...filteredLocations].sort((a, b) => {
      const genA = parseInt(a.generation) || 0;
      const genB = parseInt(b.generation) || 0;
      return genA - genB;
    });

    for (let i = 0; i < sortedLocations.length - 1; i++) {
      if (sortedLocations[i].coordinates && sortedLocations[i + 1].coordinates) {
        paths.push({
          from: sortedLocations[i].coordinates,
          to: sortedLocations[i + 1].coordinates,
          fromName: sortedLocations[i].name,
          toName: sortedLocations[i + 1].name,
          generation: sortedLocations[i].generation
        });
      }
    }
    return paths;
  };

  const exportMap = () => {
    alert('Map export feature - implement with html2canvas');
  };

  if (loading) {
    return (
      <div className="map-loading">
        <div className="spinner"></div>
        <p>Analyzing documents for locations...</p>
      </div>
    );
  }

  return (
    <div className="family-migration-map-container">
      <div className="map-header">
        <h2>📍 {familyName} Migration Map</h2>
        <div className="map-controls">
          <select 
            value={selectedGeneration} 
            onChange={(e) => setSelectedGeneration(e.target.value)}
            className="generation-filter"
          >
            <option value="all">All Generations</option>
            <option value="1">1st Generation</option>
            <option value="2">2nd Generation</option>
            <option value="3">3rd Generation</option>
            <option value="4">4th Generation</option>
            <option value="5">5th Generation</option>
            <option value="6">6th Generation</option>
            <option value="7">7th Generation</option>
          </select>

          <select 
            value={mapType} 
            onChange={(e) => setMapType(e.target.value)}
            className="map-type-selector"
          >
            <option value="markers">Location Markers</option>
            <option value="migration">Migration Paths</option>
            <option value="density">Density Map</option>
          </select>

          <button onClick={exportMap} className="export-map-btn">
            📷 Export Map
          </button>
        </div>
      </div>

      <div className="map-wrapper">
        <MapContainer
          ref={mapRef}
          center={[36.3573, -77.0012]}
          zoom={7}
          style={{ height: '600px', width: '100%' }}
          className="family-map"
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap contributors'
          />

          {mapType === 'markers' && filteredLocations.map((location, idx) => (
            <Marker
              key={idx}
              position={location.coordinates}
              icon={createCustomIcon(location)}
            >
              <Popup className="location-popup">
                <div className="popup-content">
                  <h3>{location.name}</h3>
                  <div className="popup-details">
                    <p><strong>📅 Period:</strong> {location.period}</p>
                    <p><strong>👥 Generation:</strong> {location.generation}</p>
                    {location.members && location.members.length > 0 && (
                      <>
                        <p><strong>👤 Family Members:</strong></p>
                        <ul className="member-list">
                          {location.members.map((member, midx) => (
                            <li key={midx}>{member}</li>
                          ))}
                        </ul>
                      </>
                    )}
                    {location.significance && (
                      <p className="significance">
                        <strong>📜 Significance:</strong> {location.significance}
                      </p>
                    )}
                    {location.documents && location.documents.length > 0 && (
                      <div className="source-docs">
                        <strong>📄 Source Documents:</strong>
                        <ul>
                          {location.documents.map((doc, didx) => (
                            <li key={didx}>{doc}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}

          {mapType === 'migration' && createMigrationPaths().map((path, idx) => (
            <React.Fragment key={idx}>
              <Polyline
                positions={[path.from, path.to]}
                color={getGenerationColor(path.generation)}
                weight={3}
                opacity={0.7}
                dashArray="10, 10"
              >
                <Popup>
                  <div>
                    <strong>Migration Path</strong><br />
                    From: {path.fromName}<br />
                    To: {path.toName}<br />
                    Generation: {path.generation}
                  </div>
                </Popup>
              </Polyline>
            </React.Fragment>
          ))}

          {mapType === 'density' && filteredLocations.map((location, idx) => (
            <CircleMarker
              key={idx}
              center={location.coordinates}
              radius={Math.min(20, (location.members?.length || 1) * 5)}
              fillColor={getGenerationColor(location.generation)}
              color="#000"
              weight={1}
              opacity={0.8}
              fillOpacity={0.5}
            >
              <Popup>
                <strong>{location.name}</strong><br />
                {location.members?.length || 0} family members
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>

      <div className="map-legend">
        <h3>Legend</h3>
        <div className="legend-items">
          {[1, 2, 3, 4, 5, 6, 7].map(gen => (
            <div key={gen} className="legend-item">
              <div 
                className="legend-color" 
                style={{ backgroundColor: getGenerationColor(gen.toString()) }}
              ></div>
              <span>Generation {gen}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="map-stats">
        <h3>📊 Migration Statistics</h3>
        <div className="stats-grid">
          <div className="stat-item">
            <span className="stat-label">Total Locations:</span>
            <span className="stat-value">{locations.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">States Covered:</span>
            <span className="stat-value">
              {[...new Set(locations.map(l => l.state))].filter(Boolean).join(', ') || 'N/A'}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Time Span:</span>
            <span className="stat-value">
              {locations.length > 0 && locations[0].period ? `${locations[0].period.split('-')[0]} - Present` : 'N/A'}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Generations:</span>
            <span className="stat-value">
              {[...new Set(locations.map(l => l.generation))].filter(Boolean).length || 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FamilyMigrationMap;
